import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useParams, Navigate } from "react-router-dom";
import Dashboard from "@/components/Dashboard";
import LandingPage from "@/components/LandingPage";
import HotelConfirmation from "@/components/HotelConfirmation";
import ProgressTracker from "@/components/ProgressTracker";
import AdminDashboard from "@/components/admin/AdminDashboard";
import AdminLogin from "@/components/admin/AdminLogin";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * AUDIT FLOW STAGES:
 * 1. LANDING - User enters URL, email, and basic info
 * 2. CONFIRMATION - User verifies/edits hotel name and location
 * 3. PROCESSING - Progress tracker shows audit progress
 * 4. RESULTS - Dashboard with full audit results
 */

// Main App with Landing Page and Audit Flow
const MainApp = () => {
  const [stage, setStage] = useState("landing"); // landing | confirmation | processing | results
  const [auditData, setAuditData] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [formData, setFormData] = useState(null);
  const [auditId, setAuditId] = useState(null);
  const [isComplete, setIsComplete] = useState(false);

  // Stage 1: Landing page submit - extract hotel info
  const handleLandingSubmit = async (data) => {
    setFormData(data);
    
    try {
      // First, extract hotel information from the URL
      const extractRes = await fetch(`${API}/extract-hotel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: data.url }),
      });
      const extractedInfo = await extractRes.json();
      
      setExtractedData({
        hotelName: extractedInfo?.hotelName || extractedInfo?.name || "",
        city: extractedInfo?.city || "",
        country: extractedInfo?.country || "",
      });
      
      setStage("confirmation");
    } catch (e) {
      console.error("Error extracting hotel info:", e);
      // Still proceed to confirmation with empty data
      setExtractedData({ hotelName: "", city: "", country: "" });
      setStage("confirmation");
    }
  };

  // Stage 2: Confirmation submit - start the audit
  const handleConfirmation = async (confirmedData) => {
    setFormData(confirmedData);
    setExtractedData({
      hotelName: confirmedData.confirmedHotelName,
      city: confirmedData.confirmedCity,
      country: confirmedData.confirmedCountry,
    });
    setStage("processing");
    
    try {
      const res = await fetch(`${API}/audit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(confirmedData),
      });
      const result = await res.json();
      
      if (result.auditId) {
        setAuditId(result.auditId);
        // Start polling for completion
        pollAudit(result.auditId);
      } else if (result.status === "completed" || result.status === "partial" || result.score) {
        // Direct result from cache (completed or partial)
        setAuditData(result);
        setIsComplete(true);
        setTimeout(() => setStage("results"), 1500);
      } else if (result.error) {
        alert(`Audit error: ${result.message || result.error}`);
        setStage("landing");
      }
    } catch (e) {
      console.error("Error starting audit:", e);
      alert("Error starting audit. Please try again.");
      setStage("landing");
    }
  };

  // Poll for audit completion
  const pollAudit = async (id) => {
    try {
      const statusRes = await fetch(`${API}/audit/${id}`);
      const statusData = await statusRes.json();
      
      // Handle completed OR partial as success (partial = some data sources had issues but audit finished)
      if (statusData.status === "completed" || statusData.status === "partial") {
        setAuditData(statusData);
        setIsComplete(true);
        // Brief delay to show 100% before transitioning
        setTimeout(() => setStage("results"), 1500);
      } else if (statusData.status === "failed") {
        alert("Audit failed. Please try again.");
        setStage("landing");
      } else {
        // Still processing, poll again
        setTimeout(() => pollAudit(id), 3000);
      }
    } catch (e) {
      console.error("Error polling audit:", e);
      setTimeout(() => pollAudit(id), 5000);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    setStage("landing");
    setAuditData(null);
    setExtractedData(null);
    setFormData(null);
    setAuditId(null);
    setIsComplete(false);
  };

  const handleBackToConfirmation = () => {
    setStage("confirmation");
  };

  // Render based on current stage
  switch (stage) {
    case "confirmation":
      return (
        <HotelConfirmation
          extractedData={extractedData}
          formData={formData}
          onConfirm={handleConfirmation}
          onBack={handleBack}
        />
      );
    
    case "processing":
      return (
        <ProgressTracker
          isComplete={isComplete}
          extractedData={extractedData}
        />
      );
    
    case "results":
      return (
        <Dashboard
          data={auditData}
          extractedData={extractedData}
          formData={formData}
          onBack={handleBack}
          readOnly={false}
        />
      );
    
    default: // "landing"
      return (
        <LandingPage
          onSubmit={handleLandingSubmit}
          isLoading={false}
        />
      );
  }
};

// Shared Audit Page for /share/:token routes
const SharedAuditPage = () => {
  const { token } = useParams();
  const [auditData, setAuditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAudit = async () => {
      try {
        const res = await fetch(`${API}/share/${token}`);
        if (!res.ok) {
          setError("This report could not be found or the link has expired.");
          return;
        }
        const data = await res.json();
        setAuditData(data);
      } catch (e) {
        setError("Unable to load this report. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchAudit();
  }, [token]);

  useEffect(() => {
    if (!auditData) return;
    const hotelName = auditData?.input?.confirmedHotelName || auditData?.hotelName || "Your Hotel";
    const score = auditData?.score || auditData?.overall_score || 0;

    document.title = `${hotelName} — dhi Hotel Digital Audit`;

    const setMeta = (property, content, isName = false) => {
      const attr = isName ? "name" : "property";
      let el = document.querySelector(`meta[${attr}="${property}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, property);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("og:title", `${hotelName} — dhi Hotel Digital Audit`);
    setMeta("og:description", `Digital audit score: ${score}/100. See the full breakdown and action plan.`);
    setMeta("og:url", window.location.href);
    setMeta("og:type", "website");
    setMeta("twitter:title", `${hotelName} — dhi Hotel Digital Audit`, true);
    setMeta("twitter:description", `Digital audit score: ${score}/100. See the full breakdown and action plan.`, true);
    setMeta("twitter:card", "summary_large_image", true);
  }, [auditData]);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#F5F6FA", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ fontSize: 14, color: "rgba(10,10,26,0.4)", fontFamily: "'DM Sans', sans-serif" }}>Loading your report...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: "100vh", background: "#F5F6FA", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", maxWidth: 400, padding: 32 }}>
          <p style={{ fontSize: 16, fontWeight: 700, color: "#0A0A1A", marginBottom: 8, fontFamily: "'DM Sans', sans-serif" }}>Report not found</p>
          <p style={{ fontSize: 13, color: "rgba(10,10,26,0.45)", fontFamily: "'DM Sans', sans-serif" }}>{error}</p>
        </div>
      </div>
    );
  }

  const extractedData = {
    hotelName: auditData?.parity_snapshot?.matchedHotelName || auditData?.google_listing_snapshot?.matchedBusinessName || auditData?.input?.confirmedHotelName || "",
    city: auditData?.input?.confirmedCity || "",
    country: auditData?.input?.confirmedCountry || "",
  };

  return (
    <Dashboard
      data={auditData}
      extractedData={extractedData}
      formData={auditData?.input || {}}
      onBack={null}
      readOnly={true}
    />
  );
};

// Admin Protected Route wrapper
const AdminProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('adminToken');
  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
};

// Root App with Router
const App = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<MainApp />} />
        <Route path="/share/:token" element={<SharedAuditPage />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={
          <AdminProtectedRoute>
            <AdminDashboard />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/*" element={
          <AdminProtectedRoute>
            <AdminDashboard />
          </AdminProtectedRoute>
        } />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
